import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { useAccount } from '@/api/rest/users/useAccount';
import {
  Field,
  Fieldset,
  H4,
  Input,
  NumberInput,
  Select,
} from '@payaca/ambrosia';
import { AccountRegions } from '@payaca/types/accountTypes';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  BUILT_FORM_OPTIONS,
  CONSTRUCTION_AGE_BAND_OPTIONS,
  getWallInsulationOptions,
  HEATING_SYSTEM_OPTIONS,
  OCCUPANCY_ARCHETYPE_OPTIONS,
  isValidInsulationForConstruction,
  PROPERTY_TYPE_OPTIONS,
  WALL_CONSTRUCTION_OPTIONS,
  WINDOW_GLAZING_OPTIONS,
} from './propertyInfoOptions';
import {
  isFieldRequired,
  PropertyInfoFormValues,
  PropertyInfoVariant,
} from './propertyInfoSchema';

type PropertyInfoFieldsProps = {
  /** Which context the form is used in; drives labels and field visibility. */
  variant: PropertyInfoVariant;
};

/**
 * The property-details form fields. Must be rendered inside an ambrosia `Form`
 * whose values match {@link PropertyInfoFormValues} (it reads the form via context).
 */
export const PropertyInfoFields: FC<PropertyInfoFieldsProps> = (props) => {
  const { variant } = props;
  // The weekday-occupancy question feeds only the MCS self-consumption
  // table on UK documents, so it is hidden outside the UK.
  const account = useAccount();
  const showOccupancyQuestion = account?.region === AccountRegions.UK;
  // The electricity questions feed the solar estimate only.
  const showElectricity = useMyAccountHasFeature('hasSolarDesignTool');
  const formMethods = useFormContext<PropertyInfoFormValues>();
  const watchedWallConstruction = formMethods.watch('wallConstruction');
  // The heat loss estimate is more accurate with annual heating energy use, so
  // that variant surfaces the optional field.
  const includeHeatingEnergyUse = variant === 'heatLoss';

  return (
    <Fieldset>
      <Controller<PropertyInfoFormValues, 'propertyType'>
        name="propertyType"
        render={({ field: { name, ...attributes } }) => (
          <Field name={name}>
            <Field.Label>Property type</Field.Label>
            <Select
              multiple={false}
              options={[...PROPERTY_TYPE_OPTIONS]}
              {...attributes}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'builtForm'>
        name="builtForm"
        render={({ field: { name, ...attributes } }) => (
          <Field name={name}>
            <Field.Label>Built form</Field.Label>
            <Select
              multiple={false}
              options={[...BUILT_FORM_OPTIONS]}
              {...attributes}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'constructionAgeBand'>
        name="constructionAgeBand"
        render={({ field: { name, ...attributes } }) => (
          <Field name={name}>
            <div className="flex items-center justify-between">
              <Field.Label>Construction age band</Field.Label>
              {!isFieldRequired(variant, 'constructionAgeBand') && (
                <Field.Optional />
              )}
            </div>
            <Select
              multiple={false}
              options={[...CONSTRUCTION_AGE_BAND_OPTIONS]}
              {...attributes}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'floorAreaM2'>
        name="floorAreaM2"
        render={({ field: { name, value, onChange, onBlur, ref } }) => (
          <Field name={name}>
            <Field.Label>Floor area (m2)</Field.Label>
            <Input
              // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
              type="number"
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'numBedrooms'>
        name="numBedrooms"
        render={({ field: { name, value, onChange, onBlur, ref } }) => (
          <Field name={name}>
            <Field.Label>Number of bedrooms</Field.Label>
            <Input
              // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
              type="number"
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'heatingSystem'>
        name="heatingSystem"
        render={({ field: { name, ...attributes } }) => (
          <Field name={name}>
            <Field.Label>Heating system</Field.Label>
            <Select
              multiple={false}
              options={[...HEATING_SYSTEM_OPTIONS]}
              {...attributes}
            />
          </Field>
        )}
      />

      {includeHeatingEnergyUse && (
        <Controller<PropertyInfoFormValues, 'heatingEnergyUseKwh'>
          name="heatingEnergyUseKwh"
          render={({ field: { name, value, onChange, onBlur, ref } }) => (
            <Field name={name}>
              <div className="flex items-center justify-between">
                <Field.Label>Annual heating energy use (kWh)</Field.Label>
                <Field.Optional />
              </div>
              <Input
                // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
                type="number"
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
              />
              <Field.HelperText>
                If known (e.g. from an energy bill), this improves the heat loss
                estimate.
              </Field.HelperText>
            </Field>
          )}
        />
      )}

      <Controller<PropertyInfoFormValues, 'wallConstruction'>
        name="wallConstruction"
        render={({ field: { name, value, onChange, ...attributes } }) => (
          <Field name={name}>
            <div className="flex items-center justify-between">
              <Field.Label>Wall construction</Field.Label>
              {!isFieldRequired(variant, 'wallConstruction') && (
                <Field.Optional />
              )}
            </div>
            <Select
              multiple={false}
              options={[...WALL_CONSTRUCTION_OPTIONS]}
              value={value}
              onChange={(newValue) => {
                onChange(newValue);
                // Clear wall insulation if it's no longer valid for the new construction type
                const currentInsulation =
                  formMethods.getValues('wallInsulation');
                if (
                  !isValidInsulationForConstruction(currentInsulation, newValue)
                ) {
                  formMethods.resetField('wallInsulation', {
                    defaultValue: undefined,
                  });
                }
              }}
              {...attributes}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'wallInsulation'>
        name="wallInsulation"
        render={({ field: { name, ...attributes } }) => (
          <Field name={name}>
            <div className="flex items-center justify-between">
              <Field.Label>Wall insulation</Field.Label>
              {!isFieldRequired(variant, 'wallInsulation') && (
                <Field.Optional />
              )}
            </div>
            <Select
              multiple={false}
              options={getWallInsulationOptions(watchedWallConstruction)}
              {...attributes}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'windowGlazingType'>
        name="windowGlazingType"
        render={({ field: { name, ...attributes } }) => (
          <Field name={name}>
            <div className="flex items-center justify-between">
              <Field.Label>Window glazing type</Field.Label>
              {!isFieldRequired(variant, 'windowGlazingType') && (
                <Field.Optional />
              )}
            </div>
            <Select
              multiple={false}
              options={[...WINDOW_GLAZING_OPTIONS]}
              {...attributes}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'loftInsulationMm'>
        name="loftInsulationMm"
        render={({ field: { name, value, onChange, onBlur, ref } }) => (
          <Field name={name}>
            <div className="flex items-center justify-between">
              <Field.Label>Loft insulation (mm)</Field.Label>
              {!isFieldRequired(variant, 'loftInsulationMm') && (
                <Field.Optional />
              )}
            </div>
            <Input
              // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
              type="number"
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
            />
          </Field>
        )}
      />

      <Controller<PropertyInfoFormValues, 'numStoreys'>
        name="numStoreys"
        render={({ field: { name, value, onChange, onBlur, ref } }) => (
          <Field name={name}>
            <div className="flex items-center justify-between">
              <Field.Label>Number of storeys</Field.Label>
              <Field.Optional />
            </div>
            <Input
              // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
              type="number"
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
            />
          </Field>
        )}
      />

      {showElectricity && (
        <div className="space-y-4 border-t border-gray-200 pt-4">
          <H4>Electricity</H4>
          {showOccupancyQuestion && (
            <Controller<PropertyInfoFormValues, 'electricityOccupancyArchetype'>
              name="electricityOccupancyArchetype"
              render={({ field: { name, value, onChange, ...attributes } }) => (
                <Field name={name}>
                  <div className="flex items-center justify-between">
                    <Field.Label>Weekday occupancy</Field.Label>
                    <Field.Optional />
                  </div>
                  <Select
                    {...attributes}
                    value={value ?? undefined}
                    multiple={false}
                    options={[...OCCUPANCY_ARCHETYPE_OPTIONS]}
                    placeholder="Select occupancy"
                    onChange={onChange}
                  />
                  <Field.HelperText>
                    When someone is usually home on weekdays. Drives the MCS
                    self-consumption estimate on solar estimates.
                  </Field.HelperText>
                </Field>
              )}
            />
          )}

          <Controller<PropertyInfoFormValues, 'annualElectricityConsumptionKwh'>
            name="annualElectricityConsumptionKwh"
            render={({ field: { name, value, onChange, onBlur, ref } }) => (
              <Field name={name}>
                <div className="flex items-center justify-between">
                  <Field.Label>
                    Annual electricity consumption (kWh)
                  </Field.Label>
                  <Field.Optional />
                </div>
                <NumberInput
                  min={0}
                  step={1}
                  value={value ?? null}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                />
                <Field.HelperText>
                  From the last twelve months of electricity bills, if
                  available.
                </Field.HelperText>
              </Field>
            )}
          />
        </div>
      )}
    </Fieldset>
  );
};
